/**
 * The conversation's glyphs, traced from the canon's own inline SVGs (`docs/design/ai-buddy-2026-09-30/`
 * boards N, M and O). Decorative: every control that carries one states its own name. The tick
 * and the disclosure chevron are `shared/ui/FormGlyphs`'s, the same drawings the forms use.
 */

interface GlyphProps {
  readonly className?: string;
}

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

export function PlusGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false" {...STROKE} strokeWidth={1.8}>
      <path d="M8 3v10M3 8h10" />
    </svg>
  );
}

export function MicGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false" {...STROKE} strokeWidth={1.7}>
      <rect x="5.5" y="1.5" width="5" height="8" rx="2.5" />
      <path d="M3 7.5a5 5 0 0 0 10 0M8 12.5v2" />
    </svg>
  );
}

/** The microphone the browser blocks: board „Dyktowanie", permission denied. */
export function MicOffGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false" {...STROKE} strokeWidth={1.7}>
      <path d="M10.5 6V4a2.5 2.5 0 0 0-5 0v3.5M6.2 9.2A2.5 2.5 0 0 0 10.4 8" />
      <path d="M3 7.5a5 5 0 0 0 8.4 3.7M13 7.5c0 .6-.1 1.1-.3 1.6M8 12.5v2M2 2l12 12" />
    </svg>
  );
}

/** Nothing heard: five quiet bars. */
export function HearGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false" {...STROKE} strokeWidth={1.7}>
      <path d="M3 6.5v3M5.5 4.5v7M8 6v4M10.5 5v6M13 7v2" />
    </svg>
  );
}

/** The open ring the transcribing action turns. */
export function SpinGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false" {...STROKE} strokeWidth={1.8}>
      <path d="M8 2.5a5.5 5.5 0 1 0 5.5 5.5" />
    </svg>
  );
}

export function SendGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false" {...STROKE} strokeWidth={2}>
      <path d="M8 13V3M3.5 7.5 8 3l4.5 4.5" />
    </svg>
  );
}

export function StopGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false" fill="currentColor">
      <rect x="4.5" y="4.5" width="7" height="7" rx="1.5" />
    </svg>
  );
}

export function OutlineGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false" {...STROKE} strokeWidth={1.7}>
      <rect x="3" y="2.5" width="10" height="11" rx="2" />
      <path d="M5.5 6h5M5.5 8.5h5M5.5 11h3" />
    </svg>
  );
}

export function ProgramGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false" {...STROKE} strokeWidth={1.7}>
      <rect x="2.5" y="3.5" width="11" height="10" rx="2" />
      <path d="M2.5 6.5h11M5.5 2v3M10.5 2v3" />
    </svg>
  );
}

export function ErrorGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false" {...STROKE} strokeWidth={1.8}>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 5v3.5M8 10.8v.2" />
    </svg>
  );
}

export function ClockGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false" {...STROKE} strokeWidth={1.8}>
      <circle cx="8" cy="8.5" r="5.5" />
      <path d="M8 5.8V8.5l1.8 1.2" />
    </svg>
  );
}

export function ThumbGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false" {...STROKE} strokeWidth={1.6}>
      <path d="M5 7v7H2.5V7H5Zm0 0 2.6-4.4a1.2 1.2 0 0 1 2.2.8L9.2 6.5h3.4a1.3 1.3 0 0 1 1.3 1.6l-1.1 4.7a1.6 1.6 0 0 1-1.6 1.2H5" />
    </svg>
  );
}

/** The session row's way in: a chevron pointing at the panel it opens. */
export function ChevronGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="m6 4 4 4-4 4" />
    </svg>
  );
}

/** Two links of a chain: changes that are accepted together (board S, the new day's pair). */
export function LinkGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false" {...STROKE} strokeWidth={1.7}>
      <path d="M6.5 9.5 9.5 6.5M5 7.5 3.8 8.7a2.5 2.5 0 0 0 3.5 3.5L8.5 11M11 8.5l1.2-1.2a2.5 2.5 0 0 0-3.5-3.5L7.5 5" />
    </svg>
  );
}

/** The conversation's history (board I, the panel's header). */
export function HistoryGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false" {...STROKE} strokeWidth={1.7}>
      <path d="M2.5 8a5.5 5.5 0 1 0 1.6-3.9M2.5 3v2.5H5M8 5v3l2 1.5" />
    </svg>
  );
}

/** A spreadsheet (board T, the drop zone). */
export function SheetGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...STROKE} strokeWidth={1.6}>
      <rect x="4" y="3" width="16" height="18" rx="2.5" />
      <path d="M4 9h16M4 15h16M10 3v18" />
    </svg>
  );
}

/** A skipped row's dash (board E). */
export function SkippedGlyph({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false" {...STROKE} strokeWidth={2}>
      <path d="M4.5 8h7" />
    </svg>
  );
}
